import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Container,
  Grid,
  Icon,
  AppBar,
  Toolbar,
  Card,
  Menu,
  MenuItem,
  Fade,
  TextField,
  useMediaQuery,
  Dialog,
  DialogContent,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import { keyframes } from "@emotion/react";
import { fadeInUp, fadeInLeft, fadeInRight, scaleIn } from "../../utils/animations";
import PropTypes from "prop-types";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import PageLayout from "examples/LayoutContainers/PageLayout";
import api from "utils/axiosConfig";

import bgImage from "assets/images/bg-sign-in-basic.jpeg";
import IconImage from "assets/images/logo.png";

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(24,106,129,0.25); }
  50%      { box-shadow: 0 0 40px rgba(24,106,129,0.5); }
`;

const floatAnimation = keyframes`
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-12px); }
`;

const gradientShift = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const countUp = keyframes`
  from { opacity: 0; transform: scale(0.5); }
  to   { opacity: 1; transform: scale(1); }
`;

// ── Scroll reveal hook ──
function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(node);
        }
      },
      { threshold }
    );
    observer.observe(node);
    return () => observer.unobserve(node);
  }, [threshold]);
  return [ref, visible];
}

// ── Counter component ──
function AnimatedCounter({ end, suffix = "", duration = 2000, visible }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!visible) return;
    let start = 0;
    const step = end / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [visible, end, duration]);
  return (
    <MDTypography
      variant="h2"
      fontWeight="bold"
      color="white"
      sx={{ animation: visible ? `${countUp} 0.6s ease-out` : "none" }}
    >
      {count}
      {suffix}
    </MDTypography>
  );
}

AnimatedCounter.propTypes = {
  end: PropTypes.number.isRequired,
  suffix: PropTypes.string,
  duration: PropTypes.number,
  visible: PropTypes.bool.isRequired,
};

// ── Feature cards data ──
const features = [
  {
    icon: "library_books",
    title: "Base Documentaire",
    desc: "Accédez à l\u2019ensemble des textes réglementaires de l\u2019UEMOA : lois, décrets, instructions, circulaires et décisions.",
  },
  {
    icon: "filter_list",
    title: "Recherche Avancée",
    desc: "Filtrez par domaine, type d\u2019acte et date de publication. Recherchez par mots-clés dans l\u2019intégralité des textes.",
  },
  {
    icon: "notifications_active",
    title: "Veille & Notifications",
    desc: "Recevez des alertes en temps réel sur les nouveaux textes et mises à jour réglementaires de votre périmètre.",
  },
  {
    icon: "map",
    title: "Cartographie des Risques",
    desc: "Identifiez, évaluez et hiérarchisez vos risques sur une matrice visuelle. Suivez le risque inhérent et résiduel par domaine.",
    isNew: true,
  },
  {
    icon: "description",
    title: "Procédures Internes",
    desc: "Centralisez vos procédures avec versioning, workflow de validation et suivi des écarts par rapport aux exigences réglementaires.",
    isNew: true,
  },
  {
    icon: "dashboard",
    title: "Tableau de Bord GRC",
    desc: "Pilotez en temps réel la conformité, les risques et les plans d'action depuis un tableau de bord unifié par institution.",
    isNew: true,
  },
];

// ── Module 2 highlights ──
const module2Features = [
  {
    icon: "map",
    color: "#0A3475",
    bg: "linear-gradient(135deg,#0A3475,#1a4d8a)",
    title: "Cartographie des risques",
    desc: "Évaluez chaque risque selon son impact, sa probabilité et la qualité de vos contrôles. La matrice risque résiduel classe automatiquement vos expositions par niveau de criticité.",
    tags: ["Risque inhérent", "Risque résiduel", "Niveaux configurables"],
  },
  {
    icon: "description",
    color: "#186A81",
    bg: "linear-gradient(135deg,#186A81,#0e5a70)",
    title: "Procédures internes",
    desc: "Rédigez, versionnez et validez vos procédures internes via un workflow structuré. Identifiez les écarts entre vos pratiques et les exigences réglementaires applicables.",
    tags: ["Versioning", "Workflow", "Analyse des écarts"],
  },
  {
    icon: "fact_check",
    color: "#2E7D32",
    bg: "linear-gradient(135deg,#2E7D32,#1b5e20)",
    title: "Référentiel des exigences",
    desc: "Associez chaque exigence réglementaire à vos procédures et risques. Visualisez instantanément le taux de couverture de votre institution.",
    tags: ["Textes réglementaires", "Couverture", "Traçabilité"],
  },
  {
    icon: "assignment_turned_in",
    color: "#C8860A",
    bg: "linear-gradient(135deg,#C8860A,#a06e08)",
    title: "Plans d'action",
    desc: "Créez et suivez des plans d'action correctifs ou préventifs associés à vos risques. Définissez responsables, échéances et indicateurs de clôture.",
    tags: ["Suivi échéances", "Responsabilités", "Rapports PDF"],
  },
];
// ── Module 3 highlights ──
const module3Features = [
  {
    icon: "fact_check",
    color: "#0A3475",
    bg: "linear-gradient(135deg,#0A3475,#1a4d8a)",
    title: "Référentiel et plan annuel de contrôle",
    desc: "14 volets et leurs points de contrôle LBC/FT/FP, pondérés par criticité. Planifiez vos contrôles de l'année avec un calendrier et un suivi de réalisation.",
    tags: ["14 volets", "Points pondérés", "Calendrier annuel"],
  },
  {
    icon: "assignment",
    color: "#186A81",
    bg: "linear-gradient(135deg,#186A81,#0e5a70)",
    title: "Missions et feuille de travail",
    desc: "Créez vos missions de contrôle interne et saisissez les résultats point par point : échantillon, preuves, observations et niveau d'anomalie.",
    tags: ["Missions internes", "Preuves", "Clôture de volet"],
  },
  {
    icon: "grid_view",
    color: "#2E7D32",
    bg: "linear-gradient(135deg,#2E7D32,#1b5e20)",
    title: "Grille d'évaluation et lacunes",
    desc: "Le taux de conformité, le niveau de risque et les lacunes de conformité sont calculés automatiquement à partir des anomalies constatées.",
    tags: ["Conformité", "Risque", "Lacunes"],
  },
  {
    icon: "description",
    color: "#C8860A",
    bg: "linear-gradient(135deg,#C8860A,#a06e08)",
    title: "Rapport de contrôle",
    desc: "Rédigez le rapport de chaque volet dans un éditeur intégré, associez des superviseurs et gardez l'historique des modifications.",
    tags: ["Éditeur intégré", "Superviseurs", "Impression"],
  },
  {
    icon: "track_changes",
    color: "#7B1FA2",
    bg: "linear-gradient(135deg,#7B1FA2,#5e1580)",
    title: "Suivi des recommandations",
    desc: "Suivez l'exécution des recommandations issues de vos contrôles et des interventions externes (BCEAO, Commission bancaire, CAC) jusqu'à leur clôture.",
    tags: ["Échéances J-14 / J-7", "Preuves", "TE / PE / NE"],
  },
  {
    icon: "bar_chart",
    color: "#C62828",
    bg: "linear-gradient(135deg,#C62828,#9a1f1f)",
    title: "Tableau de bord consolidé",
    desc: "Une vue d'ensemble du plan annuel, de la conformité et du risque par volet, et du taux d'exécution des recommandations, avec édition imprimable.",
    tags: ["Indicateurs", "Par volet", "Direction Générale"],
  },
];
// ── Demo steps ──
const demoSteps = [
  {
    icon: "person_add",
    title: "1. Créez votre compte",
    desc: "Inscrivez-vous avec vos identifiants institutionnels et accédez à votre espace dédié en quelques secondes.",
  },
  {
    icon: "search",
    title: "2. Explorez les textes",
    desc: "Parcourez la base documentaire avec des filtres avancés par domaine, type et date pour trouver ce dont vous avez besoin.",
  },
  {
    icon: "download",
    title: "3. Téléchargez & partagez",
    desc: "Téléchargez les documents PDF officiels et partagez-les au sein de votre équipe de conformité.",
  },
  {
    icon: "notifications",
    title: "4. Restez informé",
    desc: "Activez les notifications pour ne jamais manquer une nouveauté réglementaire impactant votre institution.",
  },
];

function HomePage() {
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isMobile = useMediaQuery("(max-width:450px)");
  const navigate = useNavigate();

  const [featRef, featVisible] = useReveal(0.1);
  const [statsRef, statsVisible] = useReveal(0.2);
  const [m2Ref, m2Visible] = useReveal(0.1);
  const [m3Ref, m3Visible] = useReveal(0.1);
  const [demoRef, demoVisible] = useReveal(0.1);
  const [pbRef, pbVisible] = useReveal(0.15);
  const [ctaRef, ctaVisible] = useReveal(0.2);
  const [nlRef, nlVisible] = useReveal(0.15);
  const [nlEmail, setNlEmail] = useState("");
  const [nlSubmitted, setNlSubmitted] = useState(false);
  const [nlError, setNlError] = useState("");

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <PageLayout>
      <Helmet>
        <title>ConformIMF - Veille Réglementaire UEMOA pour les IMF</title>
        <meta
          name="description"
          content="ConformIMF est la plateforme digitale de veille réglementaire UEMOA pour les institutions de microfinance. Centralisez, analysez et maîtrisez votre conformité réglementaire."
        />
        <meta property="og:title" content="ConformIMF - Veille Réglementaire UEMOA pour les IMF" />
        <meta
          property="og:description"
          content="La plateforme digitale qui aide les institutions de microfinance à piloter leur conformité réglementaire, réduire leurs risques et accélérer leur croissance dans l'espace UEMOA."
        />
        <meta property="og:url" content="https://conformimf.com/" />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content="ConformIMF - Veille Réglementaire UEMOA pour les IMF" />
        <meta
          name="twitter:description"
          content="La plateforme digitale qui aide les institutions de microfinance à piloter leur conformité réglementaire dans l'espace UEMOA."
        />
      </Helmet>

      {/* ═══════ NAVBAR ═══════ */}
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: scrolled ? "rgba(255,255,255,0.96)" : "rgba(255,255,255,0.75)",
          backdropFilter: "blur(16px)",
          boxShadow: scrolled ? "0 2px 20px rgba(10,52,117,0.1)" : "none",
          py: scrolled ? 0.5 : 1,
          transition: "all 0.4s cubic-bezier(0.4,0,0.2,1)",
          zIndex: 1100,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ justifyContent: "space-between", px: "0 !important" }}>
            <MDBox display="flex" alignItems="center" sx={{ cursor: "pointer" }}>
              <MDBox
                component="img"
                src={IconImage}
                alt="Logo"
                width="200px"
                sx={{ mr: 1.5, animation: `${floatAnimation} 3s ease-in-out infinite` }}
              />
            </MDBox>
            <MDBox display="flex" alignItems="center" gap={2}>
              {isMobile ? (
                <IconButton
                  onClick={() => setMobileMenuOpen(true)}
                  sx={{
                    color: "#0A3475",
                    border: "1.5px solid rgba(10,52,117,0.2)",
                    borderRadius: "10px",
                    p: 0.8,
                    "&:hover": { backgroundColor: "rgba(10,52,117,0.06)" },
                  }}
                >
                  <Icon>menu</Icon>
                </IconButton>
              ) : (
                <>
                  <MDButton
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    variant="text"
                    color="purple"
                    size="small"
                  >
                    <Icon>help_outline</Icon>
                    <MDTypography variant="caption" sx={{ ml: 0.5 }}>
                      Aide
                    </MDTypography>
                  </MDButton>
                  <Menu anchorEl={anchorEl} open={openMenu} onClose={() => setAnchorEl(null)}>
                    <MenuItem
                      component={Link}
                      to="/documentation"
                      onClick={() => setAnchorEl(null)}
                    >
                      Documentation
                    </MenuItem>
                    <MenuItem component={Link} to="/contact" onClick={() => setAnchorEl(null)}>
                      Nous contacter
                    </MenuItem>
                  </Menu>
                  <MDButton
                    variant="gradient"
                    color="purple"
                    size="small"
                    href="/dashboard"
                    sx={{
                      px: 3,
                      transition: "transform 0.3s, box-shadow 0.3s",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 6px 20px rgba(10,52,117,0.35)",
                      },
                    }}
                  >
                    Espace Utilisateur
                  </MDButton>
                </>
              )}
            </MDBox>
          </Toolbar>
        </Container>
      </AppBar>

      <MDBox sx={{ width: "100%", minHeight: "100vh", pt: 10, overflowX: "hidden" }}>
        {/* ═══════ HERO ═══════ */}
        <MDBox
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight={{ xs: "80vh", md: "92vh" }}
          sx={{
            background: "linear-gradient(-45deg, #186A81, #0A3475, #0d4a6b, #186A81)",
            backgroundSize: "300% 300%",
            animation: `${gradientShift} 12s ease infinite`,
            position: "relative",
            overflow: "hidden",
            borderRadius: { xs: 0, md: "0 0 60px 60px" },
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `url(${bgImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              opacity: 0.1,
            },
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: "-50px",
              left: "-10%",
              width: "120%",
              height: "100px",
              background: "white",
              borderRadius: "50% 50% 0 0",
            },
          }}
        >
          <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
            <Grid container justifyContent="center" textAlign="center">
              <Grid item xs={12} lg={9}>
                <Fade in timeout={800}>
                  <MDBox>
                    <MDBox
                      display="inline-flex"
                      alignItems="center"
                      sx={{
                        backgroundColor: "rgba(255,255,255,0.15)",
                        backdropFilter: "blur(10px)",
                        borderRadius: "30px",
                        px: 3,
                        py: 0.8,
                        mb: 3,
                        border: "1px solid rgba(255,255,255,0.25)",
                      }}
                    >
                      <Icon sx={{ color: "#FFD740", mr: 1, fontSize: "18px" }}>verified</Icon>
                      <MDTypography
                        variant="caption"
                        color="white"
                        sx={{ letterSpacing: 1.5, textTransform: "uppercase", fontSize: "0.7rem" }}
                      >
                        Veille · Risques · Conformité UEMOA
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </Fade>

                <MDTypography
                  variant="h1"
                  fontWeight="bold"
                  sx={{
                    mb: 1.5,
                    fontSize: { xs: "2.2rem", sm: "3rem", md: "3.8rem", lg: "4.2rem" },
                    lineHeight: 1.12,
                    animation: `${fadeInUp} 1s ease-out`,
                    textShadow: "0 4px 30px rgba(0,0,0,0.3)",
                  }}
                >
                  <span style={{ color: "#ffffff" }}>Conform</span>
                  <span style={{ color: "#FFD740" }}>IMF</span>
                </MDTypography>

                <MDTypography
                  variant="h4"
                  color="white"
                  fontWeight="medium"
                  sx={{
                    mb: 2.5,
                    fontSize: { xs: "1.1rem", sm: "1.5rem", md: "2rem" },
                    letterSpacing: 1,
                    animation: `${fadeInUp} 1.1s ease-out`,
                    textShadow: "0 2px 16px rgba(0,0,0,0.18)",
                  }}
                >
                  Transformez la conformité en opportunités
                </MDTypography>

                <MDTypography
                  variant="body1"
                  color="white"
                  sx={{
                    opacity: 0.92,
                    mb: 5,
                    fontSize: { xs: "1.05rem", md: "1.2rem" },
                    maxWidth: "680px",
                    mx: "auto",
                    lineHeight: 1.8,
                    animation: `${fadeInUp} 1.2s ease-out`,
                  }}
                >
                  La plateforme digitale qui aide les institutions de microfinance à piloter leur
                  conformité réglementaire, réduire leurs risques et accélérer leur croissance.
                </MDTypography>

                <MDBox
                  sx={{
                    animation: `${fadeInUp} 1.4s ease-out`,
                    display: "flex",
                    gap: 2,
                    justifyContent: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <MDButton
                    variant="contained"
                    size="large"
                    href="/dashboard"
                    sx={{
                      backgroundColor: "#FFD740",
                      color: "#0A3475",
                      fontWeight: "bold",
                      px: 5,
                      py: 1.5,
                      fontSize: "1rem",
                      borderRadius: "14px",
                      transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)",
                      "&:hover": {
                        backgroundColor: "#fff",
                        transform: "translateY(-3px)",
                        boxShadow: "0 12px 35px rgba(0,0,0,0.3)",
                      },
                    }}
                  >
                    <Icon sx={{ mr: 1 }}>login</Icon>
                    Accéder à la plateforme
                  </MDButton>
                  <MDButton
                    variant="outlined"
                    size="large"
                    href="#fonctionnalites"
                    sx={{
                      color: "#fff",
                      borderColor: "rgba(255,255,255,0.5)",
                      px: 4,
                      py: 1.5,
                      fontSize: "1rem",
                      borderRadius: "14px",
                      transition: "all 0.3s",
                      "&:hover": {
                        borderColor: "#FFD740",
                        backgroundColor: "rgba(255,215,64,0.1)",
                        transform: "translateY(-3px)",
                      },
                    }}
                  >
                    <Icon sx={{ mr: 1 }}>arrow_downward</Icon>
                    Découvrir
                  </MDButton>
                </MDBox>
              </Grid>
            </Grid>
          </Container>
        </MDBox>

        {/* ═══════ STATS BAR ═══════ */}
        <Container maxWidth="lg" sx={{ mt: -5, position: "relative", zIndex: 3 }}>
          <Card
            ref={statsRef}
            sx={{
              borderRadius: "20px",
              overflow: "hidden",
              animation: statsVisible ? `${scaleIn} 0.7s ease-out` : "none",
              opacity: statsVisible ? 1 : 0,
            }}
          >
            <MDBox
              sx={{
                background: "linear-gradient(135deg, #0A3475 0%, #186A81 60%, #D89A1F 100%)",
                py: { xs: 3, md: 4 },
                px: 3,
              }}
            >
              <Grid container spacing={3} justifyContent="center" textAlign="center">
                {[
                  { value: 500, suffix: "+", label: "Textes réglementaires" },
                  { value: 8, suffix: "", label: "Pays de l\u2019UEMOA" },
                  { value: 15, suffix: "+", label: "Domaines couverts" },
                  { value: 99, suffix: "%", label: "Disponibilité" },
                ].map((stat, i) => (
                  <Grid item xs={6} md={3} key={i}>
                    <AnimatedCounter end={stat.value} suffix={stat.suffix} visible={statsVisible} />
                    <MDTypography
                      variant="caption"
                      color="white"
                      sx={{
                        opacity: 0.85,
                        mt: 0.5,
                        display: "block",
                        fontSize: "0.75rem",
                        letterSpacing: 0.3,
                      }}
                    >
                      {stat.label}
                    </MDTypography>
                  </Grid>
                ))}
              </Grid>
            </MDBox>
          </Card>
        </Container>

        {/* ═══════ FONCTIONNALITÉS ═══════ */}
        <MDBox id="fonctionnalites" ref={featRef} sx={{ py: { xs: 8, md: 12 } }}>
          <Container maxWidth="lg">
            <MDBox
              textAlign="center"
              mb={6}
              sx={{
                animation: featVisible ? `${fadeInUp} 0.6s ease-out` : "none",
                opacity: featVisible ? 1 : 0,
              }}
            >
              <MDBox
                display="inline-flex"
                alignItems="center"
                sx={{
                  backgroundColor: "#E8F4F6",
                  borderRadius: "30px",
                  px: 2.5,
                  py: 0.6,
                  mb: 2,
                }}
              >
                <Icon sx={{ color: "#0A3475", mr: 0.8, fontSize: "18px" }}>auto_awesome</Icon>
                <MDTypography
                  variant="caption"
                  color="purple"
                  sx={{
                    fontWeight: "bold",
                    letterSpacing: 1,
                    textTransform: "uppercase",
                    fontSize: "0.7rem",
                  }}
                >
                  Fonctionnalités
                </MDTypography>
              </MDBox>
              <MDTypography
                variant="h3"
                fontWeight="bold"
                mb={1}
                sx={{ fontSize: { xs: "1.5rem", md: "2.2rem" } }}
              >
                Tout ce dont vous avez besoin
              </MDTypography>
              <MDTypography
                variant="body1"
                color="text"
                sx={{ maxWidth: 600, mx: "auto", opacity: 0.8 }}
              >
                Une plateforme complète conçue pour les professionnels de la conformité
                réglementaire dans l&apos;espace UEMOA.
              </MDTypography>
            </MDBox>

            <Grid container spacing={3}>
              {features.map((f, i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Card
                    sx={{
                      p: 3.5,
                      height: "100%",
                      textAlign: "center",
                      borderRadius: "20px",
                      border: "1px solid",
                      borderColor: "grey.100",
                      cursor: "default",
                      animation: featVisible ? `${fadeInUp} ${0.4 + i * 0.15}s ease-out` : "none",
                      opacity: featVisible ? 1 : 0,
                      transition: "all 0.4s cubic-bezier(0.4,0,0.2,1)",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 20px 50px rgba(10,52,117,0.12)",
                        borderColor: "#186A81",
                      },
                    }}
                  >
                    <MDBox
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      width="65px"
                      height="65px"
                      borderRadius="16px"
                      sx={{
                        background: "linear-gradient(135deg, #186A81, #0A3475)",
                        mx: "auto",
                        mb: 2.5,
                        transition: "all 0.3s",
                        animation: `${pulseGlow} 3s ease-in-out infinite`,
                        animationDelay: `${i * 0.5}s`,
                      }}
                    >
                      <Icon sx={{ color: "#FFD740", fontSize: "32px" }}>{f.icon}</Icon>
                    </MDBox>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      gap={1}
                      mb={0.5}
                    >
                      <MDTypography variant="h6" fontWeight="bold">
                        {f.title}
                      </MDTypography>
                      {f.isNew && (
                        <MDBox
                          component="span"
                          sx={{
                            background: "linear-gradient(135deg,#C8860A,#e6a020)",
                            color: "#fff",
                            fontSize: "0.58rem",
                            fontWeight: 700,
                            letterSpacing: ".08em",
                            px: 0.8,
                            py: 0.2,
                            borderRadius: "4px",
                            textTransform: "uppercase",
                            lineHeight: 1.4,
                            flexShrink: 0,
                          }}
                        >
                          Nouveau
                        </MDBox>
                      )}
                    </MDBox>
                    <MDTypography
                      variant="body2"
                      color="text"
                      sx={{ lineHeight: 1.7, opacity: 0.8 }}
                    >
                      {f.desc}
                    </MDTypography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </MDBox>

        {/* ═══════ MODULE 2 - GRC ═══════ */}
        <MDBox
          ref={m2Ref}
          sx={{
            py: { xs: 8, md: 12 },
            background: "linear-gradient(180deg, #f0f5fb 0%, #e8f0f8 100%)",
          }}
        >
          <Container maxWidth="lg">
            <MDBox
              textAlign="center"
              mb={6}
              sx={{
                animation: m2Visible ? `${fadeInUp} 0.6s ease-out` : "none",
                opacity: m2Visible ? 1 : 0,
              }}
            >
              <MDBox
                display="inline-flex"
                alignItems="center"
                gap={1}
                sx={{
                  backgroundColor: "rgba(200,134,10,0.12)",
                  border: "1px solid rgba(200,134,10,0.3)",
                  borderRadius: "30px",
                  px: 2.5,
                  py: 0.6,
                  mb: 2,
                }}
              >
                <Icon sx={{ color: "#C8860A", fontSize: "16px" }}>new_releases</Icon>
                <MDTypography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: 1,
                    textTransform: "uppercase",
                    fontSize: "0.68rem",
                    color: "#C8860A",
                  }}
                >
                  Module 2 - Nouveau
                </MDTypography>
              </MDBox>
              <MDTypography
                variant="h3"
                fontWeight="bold"
                mb={1}
                sx={{ fontSize: { xs: "1.5rem", md: "2.2rem" } }}
              >
                Gestion des Risques & Conformité
              </MDTypography>
              <MDTypography
                variant="body1"
                color="text"
                sx={{ maxWidth: 620, mx: "auto", opacity: 0.8 }}
              >
                Au-delà de la veille documentaire, pilotez vos risques opérationnels, structurez vos
                procédures internes et suivez vos plans d&apos;action depuis une plateforme unifiée.
              </MDTypography>
            </MDBox>

            <Grid container spacing={3}>
              {module2Features.map((f, i) => (
                <Grid item xs={12} sm={6} key={i}>
                  <Card
                    sx={{
                      p: 3,
                      height: "100%",
                      borderRadius: "20px",
                      border: "1px solid",
                      borderColor: "grey.100",
                      animation: m2Visible ? `${fadeInUp} ${0.4 + i * 0.15}s ease-out` : "none",
                      opacity: m2Visible ? 1 : 0,
                      transition: "all 0.4s cubic-bezier(0.4,0,0.2,1)",
                      "&:hover": {
                        transform: "translateY(-6px)",
                        boxShadow: "0 18px 48px rgba(10,52,117,0.1)",
                        borderColor: f.color,
                      },
                    }}
                  >
                    <MDBox display="flex" gap={2.5} alignItems="flex-start">
                      <MDBox
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                          width: 52,
                          height: 52,
                          borderRadius: "14px",
                          background: f.bg,
                          flexShrink: 0,
                          boxShadow: `0 6px 20px ${f.color}40`,
                        }}
                      >
                        <Icon sx={{ color: "#fff", fontSize: "26px" }}>{f.icon}</Icon>
                      </MDBox>
                      <MDBox flex={1} minWidth={0}>
                        <MDTypography variant="h6" fontWeight="bold" mb={0.5}>
                          {f.title}
                        </MDTypography>
                        <MDTypography
                          variant="body2"
                          color="text"
                          sx={{ lineHeight: 1.7, opacity: 0.78, mb: 1.5 }}
                        >
                          {f.desc}
                        </MDTypography>
                        <MDBox display="flex" gap={0.8} flexWrap="wrap">
                          {f.tags.map((tag) => (
                            <MDBox
                              key={tag}
                              component="span"
                              sx={{
                                fontSize: "0.67rem",
                                fontWeight: 600,
                                color: f.color,
                                backgroundColor: `${f.color}14`,
                                border: `1px solid ${f.color}30`,
                                borderRadius: "6px",
                                px: 1,
                                py: 0.3,
                              }}
                            >
                              {tag}
                            </MDBox>
                          ))}
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <MDBox
              textAlign="center"
              mt={5}
              sx={{
                animation: m2Visible ? `${fadeInUp} 0.9s ease-out` : "none",
                opacity: m2Visible ? 1 : 0,
              }}
            >
              <MDButton
                variant="contained"
                size="large"
                href="/dashboard"
                sx={{
                  backgroundColor: "#0A3475",
                  color: "#FFD740",
                  fontWeight: "bold",
                  px: 5,
                  py: 1.5,
                  fontSize: "1rem",
                  borderRadius: "14px",
                  transition: "all 0.3s",
                  "&:hover": {
                    backgroundColor: "#186A81",
                    transform: "translateY(-3px)",
                    boxShadow: "0 12px 35px rgba(10,52,117,0.3)",
                  },
                }}
              >
                <Icon sx={{ mr: 1 }}>rocket_launch</Icon>
                Accéder à la plateforme GRC
              </MDButton>
            </MDBox>
          </Container>
        </MDBox>

        {/* ═══════ MODULE 3 - CONTRÔLE & AUDIT ═══════ */}
        <MDBox
          ref={m3Ref}
          sx={{
            py: { xs: 8, md: 12 },
            background: "linear-gradient(180deg, #ffffff 0%, #f4f8fb 100%)",
          }}
        >
          <Container maxWidth="lg">
            <MDBox
              textAlign="center"
              mb={6}
              sx={{
                animation: m3Visible ? `${fadeInUp} 0.6s ease-out` : "none",
                opacity: m3Visible ? 1 : 0,
              }}
            >
              <MDBox
                display="inline-flex"
                alignItems="center"
                gap={1}
                sx={{
                  backgroundColor: "rgba(200,134,10,0.12)",
                  border: "1px solid rgba(200,134,10,0.3)",
                  borderRadius: "30px",
                  px: 2.5,
                  py: 0.6,
                  mb: 2,
                }}
              >
                <Icon sx={{ color: "#C8860A", fontSize: "16px" }}>new_releases</Icon>
                <MDTypography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: 1,
                    textTransform: "uppercase",
                    fontSize: "0.68rem",
                    color: "#C8860A",
                  }}
                >
                  Module 3 - Nouveau
                </MDTypography>
              </MDBox>
              <MDTypography
                variant="h3"
                fontWeight="bold"
                mb={1}
                sx={{ fontSize: { xs: "1.5rem", md: "2.2rem" } }}
              >
                Contrôle de Conformité & Audit LBC/FT
              </MDTypography>
              <MDTypography
                variant="body1"
                color="text"
                sx={{ maxWidth: 620, mx: "auto", opacity: 0.8 }}
              >
                Planifiez vos contrôles, réalisez vos missions, évaluez la conformité de votre
                dispositif LBC/FT/FP et suivez la mise en œuvre des recommandations, de la
                planification jusqu&apos;au rapport.
              </MDTypography>
            </MDBox>

            <Grid container spacing={3}>
              {module3Features.map((f, i) => (
                <Grid item xs={12} sm={6} key={i}>
                  <Card
                    sx={{
                      p: 3,
                      height: "100%",
                      borderRadius: "20px",
                      border: "1px solid",
                      borderColor: "grey.100",
                      animation: m3Visible ? `${fadeInUp} ${0.4 + i * 0.15}s ease-out` : "none",
                      opacity: m3Visible ? 1 : 0,
                      transition: "all 0.4s cubic-bezier(0.4,0,0.2,1)",
                      "&:hover": {
                        transform: "translateY(-6px)",
                        boxShadow: "0 18px 48px rgba(10,52,117,0.1)",
                        borderColor: f.color,
                      },
                    }}
                  >
                    <MDBox display="flex" gap={2.5} alignItems="flex-start">
                      <MDBox
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                          width: 52,
                          height: 52,
                          borderRadius: "14px",
                          background: f.bg,
                          flexShrink: 0,
                          boxShadow: `0 6px 20px ${f.color}40`,
                        }}
                      >
                        <Icon sx={{ color: "#fff", fontSize: "26px" }}>{f.icon}</Icon>
                      </MDBox>
                      <MDBox flex={1} minWidth={0}>
                        <MDTypography variant="h6" fontWeight="bold" mb={0.5}>
                          {f.title}
                        </MDTypography>
                        <MDTypography
                          variant="body2"
                          color="text"
                          sx={{ lineHeight: 1.7, opacity: 0.78, mb: 1.5 }}
                        >
                          {f.desc}
                        </MDTypography>
                        <MDBox display="flex" gap={0.8} flexWrap="wrap">
                          {f.tags.map((tag) => (
                            <MDBox
                              key={tag}
                              component="span"
                              sx={{
                                fontSize: "0.67rem",
                                fontWeight: 600,
                                color: f.color,
                                backgroundColor: `${f.color}14`,
                                border: `1px solid ${f.color}30`,
                                borderRadius: "6px",
                                px: 1,
                                py: 0.3,
                              }}
                            >
                              {tag}
                            </MDBox>
                          ))}
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <MDBox
              textAlign="center"
              mt={5}
              sx={{
                animation: m3Visible ? `${fadeInUp} 0.9s ease-out` : "none",
                opacity: m3Visible ? 1 : 0,
              }}
            >
              <MDButton
                variant="contained"
                size="large"
                href="/controle"
                sx={{
                  backgroundColor: "#0A3475",
                  color: "#FFD740",
                  fontWeight: "bold",
                  px: 5,
                  py: 1.5,
                  fontSize: "1rem",
                  borderRadius: "14px",
                  transition: "all 0.3s",
                  "&:hover": {
                    backgroundColor: "#186A81",
                    transform: "translateY(-3px)",
                    boxShadow: "0 12px 35px rgba(10,52,117,0.3)",
                  },
                }}
              >
                <Icon sx={{ mr: 1 }}>rocket_launch</Icon>
                Accéder au module Contrôle & Audit
              </MDButton>
            </MDBox>
          </Container>
        </MDBox>

        {/* ═══════ COMMENT ÇA MARCHE ═══════ */}
        <MDBox
          ref={demoRef}
          sx={{
            py: { xs: 8, md: 12 },
            background: "linear-gradient(180deg, #f4f8fb 0%, #ffffff 100%)",
          }}
        >
          <Container maxWidth="lg">
            <MDBox
              textAlign="center"
              mb={8}
              sx={{
                animation: demoVisible ? `${fadeInUp} 0.6s ease-out` : "none",
                opacity: demoVisible ? 1 : 0,
              }}
            >
              <MDBox
                display="inline-flex"
                alignItems="center"
                sx={{
                  backgroundColor: "#E8F4F6",
                  borderRadius: "30px",
                  px: 2.5,
                  py: 0.6,
                  mb: 2,
                }}
              >
                <Icon sx={{ color: "#0A3475", mr: 0.8, fontSize: "18px" }}>play_circle</Icon>
                <MDTypography
                  variant="caption"
                  color="purple"
                  sx={{
                    fontWeight: "bold",
                    letterSpacing: 1,
                    textTransform: "uppercase",
                    fontSize: "0.7rem",
                  }}
                >
                  Comment ça marche
                </MDTypography>
              </MDBox>
              <MDTypography
                variant="h3"
                fontWeight="bold"
                mb={1}
                sx={{ fontSize: { xs: "1.5rem", md: "2.2rem" } }}
              >
                Démarrez en 4 étapes simples
              </MDTypography>
              <MDTypography
                variant="body1"
                color="text"
                sx={{ maxWidth: 550, mx: "auto", opacity: 0.8 }}
              >
                De l&apos;inscription à la veille automatisée, tout est pensé pour être rapide et
                intuitif.
              </MDTypography>
            </MDBox>

            <Grid container spacing={4}>
              {demoSteps.map((step, i) => (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <MDBox
                    sx={{
                      textAlign: "center",
                      animation: demoVisible
                        ? `${i % 2 === 0 ? fadeInLeft : fadeInRight} ${0.5 + i * 0.2}s ease-out`
                        : "none",
                      opacity: demoVisible ? 1 : 0,
                    }}
                  >
                    <MDBox
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      width="80px"
                      height="80px"
                      borderRadius="50%"
                      sx={{
                        background: "linear-gradient(135deg, #0A3475, #186A81)",
                        mx: "auto",
                        mb: 2.5,
                        boxShadow: "0 10px 30px rgba(10,52,117,0.2)",
                        animation: `${floatAnimation} ${3 + i * 0.5}s ease-in-out infinite`,
                        animationDelay: `${i * 0.3}s`,
                      }}
                    >
                      <Icon sx={{ color: "#FFD740", fontSize: "36px" }}>{step.icon}</Icon>
                    </MDBox>
                    <MDTypography variant="h6" fontWeight="bold" mb={1}>
                      {step.title}
                    </MDTypography>
                    <MDTypography
                      variant="body2"
                      color="text"
                      sx={{ lineHeight: 1.7, opacity: 0.8, px: 1 }}
                    >
                      {step.desc}
                    </MDTypography>
                  </MDBox>
                </Grid>
              ))}
            </Grid>
          </Container>
        </MDBox>

        {/* ═══════ INTERFACE MOCKUP ═══════ */}
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
          <Grid container spacing={6} alignItems="center">
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                animation: demoVisible ? `${fadeInLeft} 0.8s ease-out` : "none",
                opacity: demoVisible ? 1 : 0,
              }}
            >
              <MDBox
                display="inline-flex"
                alignItems="center"
                sx={{
                  backgroundColor: "#E8F4F6",
                  borderRadius: "30px",
                  px: 2.5,
                  py: 0.6,
                  mb: 2,
                }}
              >
                <Icon sx={{ color: "#0A3475", mr: 0.8, fontSize: "18px" }}>desktop_windows</Icon>
                <MDTypography
                  variant="caption"
                  color="purple"
                  sx={{
                    fontWeight: "bold",
                    letterSpacing: 1,
                    textTransform: "uppercase",
                    fontSize: "0.7rem",
                  }}
                >
                  Interface intuitive
                </MDTypography>
              </MDBox>
              <MDTypography
                variant="h3"
                fontWeight="bold"
                mb={2}
                sx={{ fontSize: { xs: "1.5rem", md: "2.2rem" } }}
              >
                Une expérience moderne et fluide
              </MDTypography>
              <MDTypography
                variant="body1"
                color="text"
                sx={{ lineHeight: 1.8, opacity: 0.8, mb: 3 }}
              >
                Notre interface a été entièrement repensée pour offrir une navigation simple, des
                filtres puissants et un accès rapide aux documents. Chaque écran est optimisé pour
                la productivité des équipes conformité.
              </MDTypography>
              {[
                "Filtrage multi-critères intelligent",
                "Téléchargement direct des PDF officiels",
                "Notifications en temps réel",
                "Tableau de bord personnalisable",
              ].map((item, i) => (
                <MDBox key={i} display="flex" alignItems="center" mb={1.5}>
                  <Icon sx={{ color: "#186A81", mr: 1.5, fontSize: "22px" }}>check_circle</Icon>
                  <MDTypography variant="body2" fontWeight="medium">
                    {item}
                  </MDTypography>
                </MDBox>
              ))}
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                animation: demoVisible ? `${fadeInRight} 0.8s ease-out` : "none",
                opacity: demoVisible ? 1 : 0,
              }}
            >
              <Card
                sx={{
                  borderRadius: "20px",
                  overflow: "hidden",
                  boxShadow: "0 25px 60px rgba(10,52,117,0.15)",
                  transition: "transform 0.4s",
                  "&:hover": { transform: "scale(1.02)" },
                }}
              >
                <MDBox
                  sx={{
                    background: "linear-gradient(135deg, #0A3475, #186A81)",
                    py: 1.5,
                    px: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <MDBox
                    sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#FF5F57" }}
                  />
                  <MDBox
                    sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#FEBC2E" }}
                  />
                  <MDBox
                    sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#28C840" }}
                  />
                  <MDBox
                    sx={{
                      ml: 2,
                      flex: 1,
                      backgroundColor: "rgba(255,255,255,0.15)",
                      borderRadius: "8px",
                      py: 0.4,
                      px: 1.5,
                    }}
                  >
                    <MDTypography
                      variant="caption"
                      color="white"
                      sx={{ opacity: 0.7, fontSize: "0.65rem" }}
                    >
                      conformimf.com/base-documentaire
                    </MDTypography>
                  </MDBox>
                </MDBox>
                <MDBox sx={{ p: 3, backgroundColor: "#fafafa" }}>
                  <MDBox
                    sx={{
                      backgroundColor: "#fff",
                      borderRadius: "12px",
                      p: 2,
                      mb: 2,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    }}
                  >
                    <MDBox display="flex" alignItems="center" gap={1} mb={1.5}>
                      <MDBox
                        sx={{
                          flex: 1,
                          height: 38,
                          backgroundColor: "#f5f5f5",
                          borderRadius: "8px",
                          display: "flex",
                          alignItems: "center",
                          px: 1.5,
                        }}
                      >
                        <Icon sx={{ color: "#999", fontSize: "18px", mr: 1 }}>search</Icon>
                        <MDTypography variant="caption" color="text" sx={{ opacity: 0.5 }}>
                          Rechercher un texte...
                        </MDTypography>
                      </MDBox>
                      <MDBox
                        sx={{
                          height: 38,
                          px: 2,
                          backgroundColor: "#0A3475",
                          borderRadius: "8px",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <MDTypography variant="caption" color="white" sx={{ fontSize: "0.7rem" }}>
                          Filtrer
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                    {[1, 2, 3].map((n) => (
                      <MDBox
                        key={n}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          py: 1.2,
                          px: 1.5,
                          borderBottom: n < 3 ? "1px solid #f0f0f0" : "none",
                          transition: "background 0.2s",
                          borderRadius: "8px",
                          "&:hover": { backgroundColor: "#E8F4F6" },
                        }}
                      >
                        <Icon sx={{ color: "#186A81", mr: 1.5, fontSize: "20px" }}>
                          description
                        </Icon>
                        <MDBox flex={1}>
                          <MDBox
                            sx={{
                              height: 10,
                              backgroundColor: "#e0e0e0",
                              borderRadius: 5,
                              width: `${85 - n * 15}%`,
                              mb: 0.5,
                            }}
                          />
                          <MDBox
                            sx={{
                              height: 7,
                              backgroundColor: "#f0f0f0",
                              borderRadius: 5,
                              width: `${65 - n * 10}%`,
                            }}
                          />
                        </MDBox>
                        <Icon sx={{ color: "#4CAF50", fontSize: "18px" }}>download</Icon>
                      </MDBox>
                    ))}
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </Container>

        {/* ═══════ PROBLÈMES / SOLUTIONS ═══════ */}
        <MDBox
          ref={pbRef}
          sx={{
            py: { xs: 8, md: 10 },
            background: "linear-gradient(135deg, #0A3475 0%, #186A81 60%, #0d5a6e 100%)",
            backgroundSize: "200% 200%",
            animation: `${gradientShift} 10s ease infinite`,
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: "-50%",
              left: "-20%",
              width: "60%",
              height: "200%",
              background: "radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 70%)",
              borderRadius: "50%",
            },
          }}
        >
          <Container maxWidth="xl">
            <MDBox
              textAlign="center"
              mb={5}
              sx={{
                animation: pbVisible ? `${fadeInUp} 0.6s ease-out` : "none",
                opacity: pbVisible ? 1 : 0,
              }}
            >
              <MDTypography
                variant="h3"
                color="white"
                fontWeight="bold"
                sx={{ fontSize: { xs: "1.5rem", md: "2.2rem" } }}
              >
                La conformité : une contrainte... vraiment ?
              </MDTypography>
              <MDTypography
                variant="body1"
                color="white"
                sx={{ opacity: 0.75, mt: 1, maxWidth: 600, mx: "auto" }}
              >
                ConformIMF transforme chaque obligation en levier de performance.
              </MDTypography>
            </MDBox>

            <Grid container spacing={0} alignItems="stretch">
              {/* Colonne Problèmes */}
              <Grid item xs={12} md={6}>
                <MDBox
                  sx={{
                    borderRadius: { xs: "18px 18px 0 0", md: "18px 0 0 18px" },
                    px: { xs: 3, md: 5 },
                    py: { xs: 3, md: 4 },
                    height: "100%",
                    borderRight: { md: "2px solid rgba(255,215,64,0.4)" },
                    animation: pbVisible ? `${fadeInLeft} 0.7s ease-out` : "none",
                    opacity: pbVisible ? 1 : 0,
                  }}
                >
                  <MDBox display="flex" alignItems="center" mb={2}>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      width="44px"
                      height="44px"
                      borderRadius="12px"
                      sx={{ backgroundColor: "rgba(255,215,64,0.15)", mr: 2, flexShrink: 0 }}
                    >
                      <Icon sx={{ color: "#FFD740", fontSize: "24px" }}>warning_amber</Icon>
                    </MDBox>
                    <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#FFD740" }}>
                      Les défis des IMF aujourd&apos;hui
                    </MDTypography>
                  </MDBox>
                  {[
                    "Une pression réglementaire croissante",
                    "Des exigences complexes et évolutives",
                    "Des risques élevés (sanctions, non-conformité)",
                    "Des processus souvent manuels et inefficaces",
                  ].map((item, i) => (
                    <MDBox key={i} display="flex" alignItems="flex-start" mb={1.5}>
                      <Icon
                        sx={{ color: "#FFD740", fontSize: "18px", mr: 1.5, mt: 0.3, flexShrink: 0 }}
                      >
                        arrow_forward_ios
                      </Icon>
                      <MDTypography
                        variant="body2"
                        color="white"
                        sx={{ opacity: 0.92, lineHeight: 1.7 }}
                      >
                        {item}
                      </MDTypography>
                    </MDBox>
                  ))}
                  <MDBox
                    sx={{
                      mt: 3,
                      p: 2,
                      borderRadius: "12px",
                      backgroundColor: "rgba(255,255,255,0.07)",
                      borderLeft: "3px solid #FFD740",
                    }}
                  >
                    <MDTypography
                      variant="body2"
                      color="white"
                      sx={{ fontStyle: "italic", opacity: 0.85 }}
                    >
                      <b>Résultat :</b> perte de temps, coûts élevés, opportunités manquées
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Grid>

              {/* Colonne Solution */}
              <Grid item xs={12} md={6}>
                <MDBox
                  sx={{
                    borderRadius: { xs: "0 0 18px 18px", md: "0 18px 18px 0" },
                    px: { xs: 3, md: 5 },
                    py: { xs: 3, md: 4 },
                    height: "100%",
                    backgroundColor: "rgba(255,215,64,0.12)",
                    border: "1px solid rgba(255,215,64,0.25)",
                    animation: pbVisible ? `${fadeInRight} 0.7s ease-out` : "none",
                    opacity: pbVisible ? 1 : 0,
                  }}
                >
                  <MDBox display="flex" alignItems="center" mb={2}>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      width="44px"
                      height="44px"
                      borderRadius="12px"
                      sx={{ backgroundColor: "rgba(255,215,64,0.2)", mr: 2, flexShrink: 0 }}
                    >
                      <Icon sx={{ color: "#FFD740", fontSize: "24px" }}>lightbulb</Icon>
                    </MDBox>
                    <MDTypography variant="h5" fontWeight="bold" color="white">
                      ConformIMF change la donne
                    </MDTypography>
                  </MDBox>
                  <MDTypography
                    variant="body2"
                    color="white"
                    sx={{ opacity: 0.88, mb: 2.5, lineHeight: 1.7 }}
                  >
                    Notre plateforme transforme la conformité en un outil stratégique de pilotage et
                    de performance pour votre institution.
                  </MDTypography>
                  {[
                    "Centraliser vos obligations réglementaires",
                    "Automatiser vos contrôles et reportings",
                    "Suivre vos risques en temps réel",
                    "Renforcer votre gouvernance",
                  ].map((item, i) => (
                    <MDBox key={i} display="flex" alignItems="center" mb={1.5}>
                      <Icon sx={{ color: "#FFD740", fontSize: "18px", mr: 1.5, flexShrink: 0 }}>
                        check_circle
                      </Icon>
                      <MDTypography
                        variant="body2"
                        color="white"
                        fontWeight="medium"
                        sx={{ opacity: 0.95, lineHeight: 1.7 }}
                      >
                        {item}
                      </MDTypography>
                    </MDBox>
                  ))}
                </MDBox>
              </Grid>
            </Grid>
          </Container>
        </MDBox>

        {/* ═══════ CTA FINAL ═══════ */}
        <MDBox
          ref={ctaRef}
          sx={{
            py: { xs: 8, md: 10 },
            background: "linear-gradient(135deg, #D89A1F 0%, #0A3475 50%, #186A81 100%)",
            backgroundSize: "200% 200%",
            animation: `${gradientShift} 10s ease infinite`,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Container maxWidth="md" sx={{ position: "relative", zIndex: 2 }}>
            <MDBox
              textAlign="center"
              sx={{
                animation: ctaVisible ? `${scaleIn} 0.7s ease-out` : "none",
                opacity: ctaVisible ? 1 : 0,
              }}
            >
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                width="72px"
                height="72px"
                borderRadius="20px"
                mx="auto"
                mb={3}
                sx={{ backgroundColor: "rgba(255,255,255,0.15)", backdropFilter: "blur(10px)" }}
              >
                <Icon sx={{ color: "#FFD740", fontSize: "40px !important" }}>rocket_launch</Icon>
              </MDBox>
              <MDTypography
                variant="h3"
                color="white"
                fontWeight="bold"
                mb={2}
                sx={{ fontSize: { xs: "1.5rem", md: "2.2rem" } }}
              >
                Prêt à centraliser votre veille réglementaire ?
              </MDTypography>
              <MDTypography
                variant="body1"
                color="white"
                sx={{ opacity: 0.85, mb: 4, maxWidth: 550, mx: "auto", lineHeight: 1.8 }}
              >
                Rejoignez les institutions de l&apos;UEMOA qui utilisent déjà notre plateforme pour
                rester conformes et informées.
              </MDTypography>
              <MDButton
                variant="contained"
                size="large"
                href="/authentication/sign-in"
                sx={{
                  backgroundColor: "#FFD740",
                  color: "#0A3475",
                  fontWeight: "bold",
                  px: 6,
                  py: 1.5,
                  fontSize: "1.05rem",
                  borderRadius: "14px",
                  transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)",
                  "&:hover": {
                    backgroundColor: "#fff",
                    transform: "translateY(-3px) scale(1.02)",
                    boxShadow: "0 15px 40px rgba(0,0,0,0.3)",
                  },
                }}
              >
                Commencer maintenant
              </MDButton>
            </MDBox>
          </Container>
        </MDBox>

        {/* ═══════ NEWSLETTER ═══════ */}
        <MDBox
          ref={nlRef}
          sx={{
            py: 8,
            backgroundColor: "#fff",
            borderTop: "1px solid #e8f0f7",
            borderBottom: "1px solid #e8f0f7",
          }}
        >
          <Container maxWidth="md">
            <MDBox
              textAlign="center"
              sx={{
                animation: nlVisible ? `${fadeInUp} 0.6s ease-out` : "none",
                opacity: nlVisible ? 1 : 0,
              }}
            >
              <MDBox
                display="flex"
                alignItems="center"
                justifyContent="center"
                width="64px"
                height="64px"
                borderRadius="16px"
                mx="auto"
                mb={2}
                sx={{ background: "linear-gradient(135deg, #186A81, #0A3475)" }}
              >
                <Icon sx={{ fontSize: "32px !important", color: "#FFD740" }}>email</Icon>
              </MDBox>
              <MDTypography
                variant="h3"
                fontWeight="bold"
                mb={1}
                sx={{ fontSize: { xs: "1.5rem", md: "2.2rem" } }}
              >
                Restez informé
              </MDTypography>
              <MDTypography
                variant="body1"
                color="text"
                sx={{ opacity: 0.7, maxWidth: 500, mx: "auto", mb: 4, lineHeight: 1.8 }}
              >
                Recevez les dernières actualités réglementaires, les mises à jour de la plateforme
                et les alertes de conformité directement dans votre boîte mail.
              </MDTypography>

              {nlSubmitted ? (
                <MDBox
                  sx={{
                    animation: `${scaleIn} 0.4s ease-out`,
                    p: 3,
                    borderRadius: "16px",
                    backgroundColor: "#E8F4F6",
                    maxWidth: 480,
                    mx: "auto",
                  }}
                >
                  <Icon sx={{ fontSize: "40px !important", color: "#4CAF50", mb: 1 }}>
                    check_circle
                  </Icon>
                  <MDTypography variant="h6" fontWeight="bold" display="block" mb={0.5}>
                    Merci pour votre inscription !
                  </MDTypography>
                  <MDTypography variant="body2" color="text" sx={{ opacity: 0.7 }}>
                    Vous recevrez bientôt nos prochaines communications.
                  </MDTypography>
                </MDBox>
              ) : (
                <MDBox
                  component="form"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    setNlError("");
                    try {
                      await api.post("/api/newsletter/", { email: nlEmail.trim() });
                      setNlSubmitted(true);
                    } catch (err) {
                      const msg = err.response?.data?.detail || "Une erreur est survenue.";
                      setNlError(msg);
                    }
                  }}
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 1.5,
                    maxWidth: 520,
                    mx: "auto",
                    alignItems: "stretch",
                  }}
                >
                  <TextField
                    fullWidth
                    type="email"
                    placeholder="Votre adresse email"
                    value={nlEmail}
                    onChange={(e) => setNlEmail(e.target.value)}
                    required
                    inputProps={{ maxLength: 255 }}
                    sx={{
                      flex: 1,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        backgroundColor: "#fafafa",
                        "&:hover fieldset": { borderColor: "#186A81" },
                        "&.Mui-focused fieldset": { borderColor: "#0A3475" },
                      },
                    }}
                  />
                  <MDButton
                    type="submit"
                    variant="gradient"
                    color="purple"
                    sx={{
                      px: 4,
                      py: 1.5,
                      borderRadius: "12px",
                      whiteSpace: "nowrap",
                      fontSize: "0.95rem",
                      transition: "all 0.3s",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 8px 25px rgba(10,52,117,0.25)",
                      },
                    }}
                  >
                    <Icon sx={{ mr: 0.5 }}>send</Icon>
                    S&apos;inscrire
                  </MDButton>
                </MDBox>
              )}

              {nlError && (
                <MDTypography
                  variant="body2"
                  sx={{ color: "#d32f2f", mt: 1.5, fontWeight: "medium" }}
                >
                  {nlError}
                </MDTypography>
              )}

              <MDTypography
                variant="caption"
                color="text"
                sx={{ opacity: 0.5, mt: 2.5, display: "block" }}
              >
                Pas de spam. Désabonnement possible à tout moment.
              </MDTypography>
            </MDBox>
          </Container>
        </MDBox>

        {/* ═══════ FOOTER ═══════ */}
        <MDBox component="footer" sx={{ py: 5, backgroundColor: "#fafafa" }}>
          <Container maxWidth="lg">
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={4}>
                <MDBox display="flex" alignItems="center" mb={1}>
                  <MDBox component="img" src={IconImage} alt="Logo" width="100%" sx={{ mr: 1 }} />
                </MDBox>
              </Grid>
              <Grid item xs={12} md={4} textAlign="center">
                <MDTypography variant="caption" color="text" sx={{ opacity: 0.6 }}>
                  &copy; 2026 ConformIMF - Guy Modeste EHOULO - Tous droits réservés
                </MDTypography>
              </Grid>
              <Grid item xs={12} md={4} textAlign={{ xs: "left", md: "right" }}>
                <MDBox display="flex" gap={2} justifyContent={{ xs: "flex-start", md: "flex-end" }}>
                  {[
                    { label: "Mentions légales", to: "/mentions-legales" },
                    { label: "Confidentialité", to: "/politique-confidentialite" },
                    { label: "Contact", to: "/contact" },
                  ].map((link, i) => (
                    <MDTypography
                      key={i}
                      component={Link}
                      to={link.to}
                      variant="caption"
                      color="text"
                      sx={{
                        opacity: 0.6,
                        cursor: "pointer",
                        textDecoration: "none",
                        transition: "all 0.2s",
                        "&:hover": { opacity: 1, color: "#0A3475" },
                      }}
                    >
                      {link.label}
                    </MDTypography>
                  ))}
                </MDBox>
              </Grid>
            </Grid>
          </Container>
        </MDBox>
      </MDBox>
      {/* ═══════ MOBILE MENU MODAL ═══════ */}
      <Dialog
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "20px",
            width: "92%",
            maxWidth: 360,
            mx: "auto",
            mt: "80px",
            alignSelf: "flex-start",
            overflow: "hidden",
          },
        }}
        sx={{ "& .MuiBackdrop-root": { backdropFilter: "blur(4px)" } }}
      >
        <DialogContent sx={{ p: 0 }}>
          {/* En-tête */}
          <MDBox
            sx={{
              background: "linear-gradient(135deg, #0A3475, #186A81)",
              py: 2.5,
              px: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <MDTypography variant="h6" color="white" fontWeight="bold">
              Menu
            </MDTypography>
            <IconButton
              onClick={() => setMobileMenuOpen(false)}
              sx={{ color: "rgba(255,255,255,0.8)", p: 0.5 }}
            >
              <Icon>close</Icon>
            </IconButton>
          </MDBox>

          {/* Liens */}
          <List disablePadding>
            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to="/documentation"
                onClick={() => setMobileMenuOpen(false)}
                sx={{
                  py: 0.75,
                  px: 3,
                  "&:hover": { backgroundColor: "#E8F4F6" },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Icon sx={{ color: "#186A81" }}>menu_book</Icon>
                </ListItemIcon>
                <ListItemText
                  primary="Documentation"
                  primaryTypographyProps={{ fontWeight: "medium", color: "#0A3475" }}
                />
                <Icon sx={{ color: "#999", fontSize: "18px" }}>chevron_right</Icon>
              </ListItemButton>
            </ListItem>

            <Divider sx={{ mx: 3 }} />

            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to="/contact"
                onClick={() => setMobileMenuOpen(false)}
                sx={{
                  py: 0.75,
                  px: 3,
                  "&:hover": { backgroundColor: "#E8F4F6" },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Icon sx={{ color: "#186A81" }}>mail_outline</Icon>
                </ListItemIcon>
                <ListItemText
                  primary="Nous contacter"
                  primaryTypographyProps={{ fontWeight: "medium", color: "#0A3475" }}
                />
                <Icon sx={{ color: "#999", fontSize: "18px" }}>chevron_right</Icon>
              </ListItemButton>
            </ListItem>
          </List>

          {/* Bouton Espace Utilisateur */}
          <MDBox sx={{ p: 3, pt: 1.5 }}>
            <MDButton
              variant="gradient"
              color="purple"
              fullWidth
              onClick={() => {
                setMobileMenuOpen(false);
                navigate("/dashboard");
              }}
              sx={{
                py: 1.5,
                borderRadius: "12px",
                fontSize: "0.95rem",
                fontWeight: "bold",
                transition: "all 0.3s",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 25px rgba(10,52,117,0.3)",
                },
              }}
            >
              <Icon sx={{ mr: 1 }}>person</Icon>
              Espace Utilisateur
            </MDButton>
          </MDBox>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}

export default HomePage;
